import React from "react";
import { Home, FileSignature, Briefcase, HeartPulse, ScrollText, MapPin, Clock, FileCheck2, Camera, CalendarClock, ShieldCheck } from "lucide-react";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { PageHero, SectionHeading, FeatureCard, CtaBanner } from "@/components/marketing/blocks";
import { useSeo } from "@/hooks/useSeo";

const OFFERINGS = [
  { icon: Home, title: "Real Estate & Loan Signings", desc: "Notarization for closings, refinances, and loan document packages." },
  { icon: FileSignature, title: "Powers of Attorney", desc: "Notarization of general, durable, and limited power of attorney documents." },
  { icon: ScrollText, title: "Affidavits & Sworn Statements", desc: "Careful notarization of affidavits and statements made under oath." },
  { icon: HeartPulse, title: "Healthcare Directives", desc: "Notarization of medical powers of attorney and advance directives." },
  { icon: Briefcase, title: "Business Documents", desc: "Notarization for contracts, authorizations, and corporate paperwork." },
];

const WHY = [
  { icon: MapPin, title: "Mobile & On-Demand", desc: "We come to your home, office, or agreed location across the Memphis area." },
  { icon: Clock, title: "Flexible Scheduling", desc: "Appointments arranged around your timeline, including time-sensitive signings." },
  { icon: Camera, title: "Photo-Documented Service", desc: "Where applicable, field photo evidence can be securely tied to the appointment, helping create a documented record of the service performed." },
  { icon: FileCheck2, title: "Careful Verification", desc: "Identity and documents are reviewed thoroughly for a clean, accurate notarization." },
];

const RECORD_POINTS = [
  { icon: CalendarClock, title: "Appointment Details", desc: "The assignment, appointment, and provider remain connected in one record." },
  { icon: Camera, title: "Photo Documentation", desc: "Where appropriate, photo evidence can be captured and associated with the appointment." },
  { icon: MapPin, title: "Field Verification", desc: "Relevant service information can include time and location data." },
  { icon: ShieldCheck, title: "Complete Record", desc: "Documentation remains organized with the assignment for easier review and accountability." },
];

export default function NotaryServices() {
  const origin = "https://artadmin.net";
  useSeo({
    title: "Mobile Notary Services in Memphis, TN | AR Process Service and Notary",
    description:
      "Mobile and on-demand notary services in Memphis and Shelby County, TN. Real estate closings, powers of attorney, affidavits, and business document notarization.",
    canonicalPath: "/notary-services",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Notary Services",
      provider: { "@type": "LegalService", "@id": "https://artadmin.net/#business", name: "AR Process Service and Notary" },
      areaServed: "Memphis Metro, Shelby County, Tennessee",
      url: `${origin}/notary-services`,
    },
  });

  return (
    <MarketingLayout>
      <PageHero
        overline="Notary Services"
        title="Mobile &amp; On-Demand Notary Services"
        subtitle="Convenient, professional notarization across the Memphis metro area and Shelby County — we travel to you for real estate, legal, and business documents."
        ctaTo="/notary"
        ctaLabel="Start Notary Request"
      />

      <section className="px-5 py-14 sm:px-8 lg:py-20">
        <SectionHeading overline="What we notarize" title="Documents we handle" />
        <div className="mx-auto mt-10 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {OFFERINGS.map((o) => (
            <FeatureCard key={o.title} icon={o.icon} title={o.title}>
              {o.desc}
            </FeatureCard>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white px-5 py-14 sm:px-8 lg:py-20">
        <SectionHeading overline="Why choose us" title="Notarization done right" subtitle="Professional, convenient notarization backed by a documented record of the service." />
        <div className="mx-auto mt-10 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w) => (
            <FeatureCard key={w.title} icon={w.icon} title={w.title}>
              {w.desc}
            </FeatureCard>
          ))}
        </div>
      </section>

      {/* Documentation capability — accountability without legal claims about proof of notarization */}
      <section className="bg-slate-900 px-5 py-16 sm:px-8 lg:py-24" data-testid="notary-record-feature">
        <div className="mx-auto max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-widest text-sky-400 sm:text-sm">The ARPS difference</p>
          <h2 className="mt-3 max-w-3xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            More than a completed appointment. A documented service record.
          </h2>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-slate-300">
            With AR Process Service, eligible notary appointments can include field documentation captured as part of the service record.
          </p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {RECORD_POINTS.map((p) => (
              <div key={p.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:bg-white/10">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-sky-500/15">
                  <p.icon className="h-6 w-6 text-sky-400" />
                </div>
                <h3 className="font-display text-lg font-semibold text-white">{p.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-300">{p.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 border-t border-white/10 pt-8 font-display text-xl font-semibold text-white sm:text-2xl">
            Know the service was completed. Have the record to show it.
          </p>
        </div>
      </section>

      <CtaBanner title="Need a document notarized?" subtitle="Start a service request and we'll arrange a convenient mobile notary appointment." ctaTo="/notary" ctaLabel="Start Notary Request" />
    </MarketingLayout>
  );
}
